import {
  ERROR_MESSAGES
} from '../constants';

/**
 * Password Strength Requirements
 */
export interface PasswordRequirement {
  regex: RegExp;
  message: string;
  label: string;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    regex: /.{8,}/,
    message: ERROR_MESSAGES.passwordTooShort,
    label: 'Mínimo 8 caracteres',
  },
  {
    regex: /[A-Z]/,
    message: ERROR_MESSAGES.passwordMissingUppercase,
    label: 'Uma letra maiúscula',
  },
  {
    regex: /[a-z]/,
    message: ERROR_MESSAGES.passwordMissingLowercase,
    label: 'Uma letra minúscula',
  },
  {
    regex: /[0-9]/,
    message: ERROR_MESSAGES.passwordMissingNumber,
    label: 'Um número',
  },
  {
    regex: /[^A-Za-z0-9]/,
    message: ERROR_MESSAGES.passwordMissingSpecial,
    label: 'Um caractere especial',
  },
];

/**
 * Hook para validação de formulários
 *
 * @example
 * ```tsx
 * const { validateEmail, validatePassword, checkPasswordRequirements } = useFormValidation();
 *
 * const emailError = validateEmail(email);
 * const passwordError = validatePassword(password);
 * const requirements = checkPasswordRequirements(password);
 * ```
 */
export const useFormValidation = () => {
  const validateEmail = (email: string): string | null => {
    if (!email) {
      return ERROR_MESSAGES.emailRequired;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return ERROR_MESSAGES.emailInvalid;
    }

    return null;
  };

  /**
   * Validates password with strong requirements (for registration)
   * Returns the first error found, or null if valid
   */
  const validatePassword = (password: string): string | null => {
    if (!password) {
      return ERROR_MESSAGES.passwordRequired;
    }

    // Check all requirements and return first error
    for (const requirement of PASSWORD_REQUIREMENTS) {
      if (!requirement.regex.test(password)) {
        return requirement.message;
      }
    }

    return null;
  };

  /**
   * Validates password for login (simpler validation)
   * Only checks if password is not empty
   */
  const validateLoginPassword = (password: string): string | null => {
    if (!password) {
      return ERROR_MESSAGES.passwordRequired;
    }

    return null;
  };

  /**
   * Checks which password requirements are met
   * Returns an array of booleans for each requirement
   */
  const checkPasswordRequirements = (password: string): boolean[] => {
    return PASSWORD_REQUIREMENTS.map(req => req.regex.test(password));
  };

  /**
   * Calculates password strength (0-100)
   */
  const calculatePasswordStrength = (password: string): number => {
    if (!password) return 0;

    const metRequirements = checkPasswordRequirements(password);
    const metCount = metRequirements.filter(Boolean).length;

    return Math.round((metCount / PASSWORD_REQUIREMENTS.length) * 100);
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword) {
      return 'Confirmação de senha é obrigatória';
    }

    if (password !== confirmPassword) {
      return 'As senhas não coincidem';
    }

    return null;
  };

  const validateName = (name: string): string | null => {
    if (!name) {
      return 'Nome é obrigatório';
    }

    if (name.length < 3) {
      return 'Nome deve ter no mínimo 3 caracteres';
    }

    return null;
  };

  const validatePhone = (phone: string): string | null => {
    if (!phone) {
      return 'Telefone é obrigatório';
    }

    const numbers = phone.replace(/\D/g, '');
    if (numbers.length < 10) {
      return 'Telefone inválido';
    }

    return null;
  };

  return {
    validateEmail,
    validatePassword,
    validateLoginPassword,
    validateConfirmPassword,
    validateName,
    validatePhone,
    checkPasswordRequirements,
    calculatePasswordStrength,
  };
};
